import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import LoadingButton from "components/LoadingButton";
import { makeStyles } from "@mui/styles";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";

const useStyles = makeStyles((theme) => ({
  modalTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "8px",
  },
  modalContent: {
    paddingTop: "16px",
    paddingBottom: "16px",
    minWidth: "400px",
  },
  modalSubtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "20px",
  },
  modalTextField: {
    marginBottom: "16px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
      fontSize: "14px",
    },
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    padding: "0 24px 24px 24px",
    justifyContent: "flex-end",
  },
  requirementsList: {
    marginTop: "16px",
    marginBottom: "16px",
    padding: "12px 16px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  requirementItem: {
    fontSize: "13px",
    color: "#666",
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  requirementValid: {
    color: "#4caf50",
  },
  requirementInvalid: {
    color: "#666",
  },
}));

const ResetPasswordModal = ({ open, email, onSuccess, onLogout }: any) => {
  const classes = useStyles();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 🧠 track mount state
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Password validation
  const passwordValidation = {
    minLength: newPassword.length >= 12,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword),
  };

  const isPasswordValid =
    passwordValidation.minLength &&
    passwordValidation.hasUppercase &&
    passwordValidation.hasLowercase &&
    passwordValidation.hasNumber &&
    passwordValidation.hasSpecial;

  const passwordsMatch =
    newPassword === confirmPassword && confirmPassword !== "";

  const handleSubmit = async () => {
    setError("");

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password requirements
    if (!isPasswordValid) {
      setError("Password does not meet the required criteria");
      return;
    }

    try {
      setLoading(true);
      await onSuccess({ email, newPassword });
    } catch (err) {
      if (isMounted.current)
        setError(err?.message || "Failed to reset password");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter" && isPasswordValid && passwordsMatch) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: "12px",
          minWidth: "500px",
        },
      }}
    >
      <DialogTitle className={classes.modalTitle}>
        Reset Your Password
      </DialogTitle>
      <DialogContent className={classes.modalContent}>
        <Typography className={classes.modalSubtitle}>
          You are required to reset your password before continuing. Please
          create a new password that meets the security requirements.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ marginBottom: "16px" }}>
            {error}
          </Alert>
        )}

        {/* Email Field (Read-only) */}
        <TextField
          fullWidth
          label="Email Address"
          value={email}
          disabled
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon sx={{ color: "#667eea" }} />
              </InputAdornment>
            ),
          }}
          className={classes.modalTextField}
        />

        {/* New Password Field */}
        <TextField
          fullWidth
          label="New Password"
          type={showNewPassword ? "text" : "password"}
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setError("");
          }}
          onKeyPress={handleKeyPress}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: "#667eea" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          className={classes.modalTextField}
        />

        {/* Confirm Password Field */}
        <TextField
          fullWidth
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setError("");
          }}
          onKeyPress={handleKeyPress}
          disabled={loading}
          error={confirmPassword !== "" && !passwordsMatch}
          helperText={
            confirmPassword !== "" && !passwordsMatch
              ? "Passwords do not match"
              : ""
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: "#667eea" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          className={classes.modalTextField}
        />

        {/* Password Requirements */}
        <Box className={classes.requirementsList}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}
          >
            Password Requirements:
          </Typography>
          <Box
            className={`${classes.requirementItem} ${
              passwordValidation.minLength
                ? classes.requirementValid
                : classes.requirementInvalid
            }`}
          >
            <span>{passwordValidation.minLength ? "✓" : "○"}</span>
            <span>Minimum 12 characters</span>
          </Box>
          <Box
            className={`${classes.requirementItem} ${
              passwordValidation.hasUppercase
                ? classes.requirementValid
                : classes.requirementInvalid
            }`}
          >
            <span>{passwordValidation.hasUppercase ? "✓" : "○"}</span>
            <span>At least 1 uppercase letter</span>
          </Box>
          <Box
            className={`${classes.requirementItem} ${
              passwordValidation.hasLowercase
                ? classes.requirementValid
                : classes.requirementInvalid
            }`}
          >
            <span>{passwordValidation.hasLowercase ? "✓" : "○"}</span>
            <span>At least 1 lowercase letter</span>
          </Box>
          <Box
            className={`${classes.requirementItem} ${
              passwordValidation.hasNumber
                ? classes.requirementValid
                : classes.requirementInvalid
            }`}
          >
            <span>{passwordValidation.hasNumber ? "✓" : "○"}</span>
            <span>At least 1 number</span>
          </Box>
          <Box
            className={`${classes.requirementItem} ${
              passwordValidation.hasSpecial
                ? classes.requirementValid
                : classes.requirementInvalid
            }`}
          >
            <span>{passwordValidation.hasSpecial ? "✓" : "○"}</span>
            <span>At least 1 special character</span>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <LoadingButton
          onClick={onLogout}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            minWidth: "120px",
            padding: "10px 24px",
          }}
        >
          Logout
        </LoadingButton>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          disabled={!isPasswordValid || !passwordsMatch}
          variant="contained"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            minWidth: "140px",
            padding: "10px 24px",
          }}
        >
          Reset Password
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ResetPasswordModal;
